import cx from "classnames";
import { useEffect, useRef, useState } from "react";
import { TEAM_MEMBERS_LIST_V3 } from "@/api/apiEndpoints";
import { canv } from "@/canv/global";
import { MakeRequest } from "@/canv/utils";
import { useDebounceValue } from "@/helpers/hooks/useDebounceValue";
import { useOnScreenObserver } from "@/helpers/hooks/useOnScreenObserver";
import type { Member, TeamMemberType } from "@/interface/ShareWindow/types";
import { SearchMemberListItemSkeleton } from "../SearchMemberListItemSkeleton/SearchMemberListItemSkeleton";
import styles from "./TeamMember.module.scss";

const LIMIT_ITEMS = 12;
export const TeamMember = ({ teamId, filter, rawFilter, onSelect }: TeamMemberType) => {
    const bottom = useRef<HTMLDivElement>(null);
    const isOnScreen = useOnScreenObserver(bottom);

    const [cursorId, setCursorId] = useState("");
    const [isNotFullyLoaded, setIsNotFullyLoaded] = useState(true);
    const [members, setMembers] = useState<Member[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const showLoader = useDebounceValue(isLoading, 200);

    const getMembers = async ({
        team,
        filterValue,
        cursor,
        limit,
    }: {
        team: number;
        filterValue: string;
        cursor: string;
        limit: number;
    }) => {
        try {
            setIsLoading(true);
            const result = await MakeRequest(TEAM_MEMBERS_LIST_V3, {
                teamId: String(team),
                filter: filterValue,
                cursor,
                limit: String(limit),
                token: canv.getInitDataProp("userToken"),
            });

            const { nextCursor: cursorValue, data } = result;

            setCursorId(String(cursorValue));

            if (data?.members) {
                const membersData = [...data.members];

                setMembers((prev) => [...prev, ...membersData]);

                if (membersData.length < LIMIT_ITEMS) {
                    setIsNotFullyLoaded(false);
                }
            }
        } catch (e) {
            console.error("Failed to fetch members", e);
        } finally {
            setIsLoading(false);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        // фикс первого раннего запроса, когда filter еще старый из-за debounce
        if (filter !== rawFilter) return;

        setMembers([]);
        setCursorId("");
        setIsNotFullyLoaded(true);

        getMembers({
            team: teamId,
            filterValue: filter,
            cursor: "",
            limit: LIMIT_ITEMS,
        });
    }, [teamId, filter]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (isOnScreen && isNotFullyLoaded && !isLoading) {
            getMembers({
                team: teamId,
                filterValue: filter,
                cursor: cursorId,
                limit: LIMIT_ITEMS,
            });
        }
    }, [isOnScreen]);

    return (
        <div className={cx(styles.teamMemberList, "scrollable")}>
            {members.map((member: Member) => (
                <button
                    type="button"
                    className={styles.shareWindowPeopleListItem}
                    key={member.accountId}
                    onClick={() => onSelect(member)}
                >
                    <div className={styles.logo}>{member.name.substring(0, 1)}</div>
                    <div className={styles.shareWindowPeopleListItemInfo}>
                        <div className={styles.name}>{member.name}</div>
                    </div>
                    <div></div>
                    <div className={styles.email}>{member.email}</div>
                </button>
            ))}
            <div style={{ minHeight: "10px" }} ref={bottom}>
                {showLoader && <SearchMemberListItemSkeleton />}
            </div>
        </div>
    );
};
